import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Plus, Trash2, Send, Ban, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { DateField, Empty, money, PageTitle, today } from './shared';

const firstOfMonth = () => today().slice(0, 8) + '01';
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function RunDialog({ open, onClose, people, settings, run, onSaved }) {
  const [periodStart, setPeriodStart] = useState(firstOfMonth());
  const [periodEnd, setPeriodEnd] = useState(today());
  const [payDate, setPayDate] = useState(today());
  const [pct, setPct] = useState('20');
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (run) {
      setPeriodStart(run.periodStart); setPeriodEnd(run.periodEnd); setPayDate(run.payDate);
      setPct(String(Math.round((run.withholdingPct || 0) * 1000) / 10));
      setLines(run.lines.map((l) => ({ ...l, hours: String(l.hours || ''), rate: String(l.rate || ''), gross: String(l.gross || '') })));
    } else {
      setPct(String(Math.round((settings?.withholdingPct ?? 0.2) * 1000) / 10));
      setLines(people.filter((p) => p.role !== 'admin').map((p) => ({
        name: p.name || p.email, email: p.email, hours: '', rate: String(settings?.rates?.[p.name || p.email] || ''), gross: '', salaried: false,
      })));
    }
  }, [open, run, people, settings]);

  const set = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const gross = (l) => (l.salaried ? r2(l.gross) : r2((Number(l.hours) || 0) * (Number(l.rate) || 0)));
  const totalGross = lines.reduce((a, l) => a + gross(l), 0);
  const withheld = r2(totalGross * (Number(pct) || 0) / 100);

  const save = async () => {
    setBusy(true);
    try {
      await ledgerApi.savePayroll({
        id: run?.id, periodStart, periodEnd, payDate, withholdingPct: (Number(pct) || 0) / 100,
        lines: lines.filter((l) => l.name && gross(l) > 0).map((l) => ({ ...l, gross: l.salaried ? Number(l.gross) : undefined })),
      });
      toast.success('Payroll run saved as a draft');
      onSaved(); onClose();
    } catch (e) {
      toast.error(apiError(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>{run ? `Edit ${run.number}` : 'New payroll run'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-3">
          <DateField label="Period start" value={periodStart} onChange={setPeriodStart} />
          <DateField label="Period end" value={periodEnd} onChange={setPeriodEnd} />
          <DateField label="Pay date" value={payDate} onChange={setPayDate} />
          <div><Label className="text-xs text-slate-500">Withholding %</Label><Input type="number" step="0.1" min="0" max="99" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 h-9 w-28" /></div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <div className="grid grid-cols-[1fr_80px_90px_70px_100px_100px_32px] gap-2 text-[11px] uppercase text-slate-400 font-semibold mb-1"><span>Employee</span><span className="text-right">Hours</span><span className="text-right">Rate</span><span className="text-center">Salary</span><span className="text-right">Gross</span><span className="text-right">Net</span><span /></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_90px_70px_100px_100px_32px] gap-2 mb-1.5 items-center">
              <Input value={l.name} onChange={(e) => set(i, { name: e.target.value })} className="h-9" />
              <Input type="number" step="0.25" value={l.hours} disabled={l.salaried} onChange={(e) => set(i, { hours: e.target.value })} className="h-9 text-right" />
              <Input type="number" step="0.01" value={l.rate} disabled={l.salaried} onChange={(e) => set(i, { rate: e.target.value })} className="h-9 text-right" />
              <div className="flex justify-center"><Checkbox checked={l.salaried} onCheckedChange={(v) => set(i, { salaried: Boolean(v) })} /></div>
              {l.salaried
                ? <Input type="number" step="0.01" value={l.gross} onChange={(e) => set(i, { gross: e.target.value })} className="h-9 text-right" />
                : <span className="text-right tabular-nums text-sm">{money(gross(l))}</span>}
              <span className="text-right tabular-nums text-sm text-slate-600">{money(gross(l) - r2(gross(l) * (Number(pct) || 0) / 100))}</span>
              <Button variant="ghost" size="icon" className="h-9 w-8" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1 mt-1" onClick={() => setLines((ls) => [...ls, { name: '', hours: '', rate: '', gross: '', salaried: false }])}><Plus className="w-3.5 h-3.5" />Add person</Button>
        </div>
        <div className="flex justify-end gap-6 text-sm tabular-nums border-t pt-3">
          <span>Gross <b>{money(totalGross)}</b></span><span>Withheld <b>{money(withheld)}</b></span><span>Net pay <b>{money(totalGross - withheld)}</b></span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#8BC53F] hover:bg-[#6FA82C]" disabled={busy || totalGross <= 0} onClick={save}>{busy ? 'Saving…' : 'Save draft'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Payroll() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { ledgerApi.payroll().then(setData).catch((e) => toast.error(apiError(e, 'Could not load payroll'))); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-payroll">
      <PageTitle title="Payroll" subtitle="Record each pay run. Posting it books the wages, the net pay from the bank and the withholding you owe.">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-payroll-btn"><Plus className="w-4 h-4" />New run</Button>
      </PageTitle>
      <Card className="p-3 border-slate-200 bg-slate-50 text-xs text-slate-600">
        This records payroll in your books. It does not pay anyone, file taxes or send deposits — use your payroll provider or bank for that, then record the run here.
      </Card>
      <Card className="border-slate-200">
        {!data ? <Empty>Loading…</Empty> : !data.runs.length ? <Empty>No payroll runs yet.</Empty> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="px-4 py-3 text-left">Run</th><th className="px-4 py-3 text-left">Period</th><th className="px-4 py-3 text-left">Pay date</th><th className="px-4 py-3 text-right">People</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">Net</th><th className="px-4 py-3 text-left">Status</th><th /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.runs.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{r.number}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.periodStart} – {r.periodEnd}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.payDate}</td>
                  <td className="px-4 py-2.5 text-right">{r.lines.length}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(r.totals.gross)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(r.totals.net)}</td>
                  <td className="px-4 py-2.5"><Badge className={`border-0 ${r.status === 'Posted' ? 'bg-emerald-100 text-emerald-700' : r.status === 'Void' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-800'}`}>{r.status}</Badge></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    {r.status === 'Draft' ? (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => { setEditing(r); setOpen(true); }}><Pencil className="w-3.5 h-3.5" />Edit</Button>
                        <Button size="sm" className="h-7 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C]" onClick={() => act(() => ledgerApi.postPayroll(r.id), `${r.number} posted to the books`)}><Send className="w-3.5 h-3.5" />Post</Button>
                      </>
                    ) : null}
                    {r.status !== 'Void' ? (
                      <Button size="sm" variant="ghost" className="h-7 gap-1 text-slate-500" onClick={() => window.confirm(`Void ${r.number}?`) && act(() => ledgerApi.voidPayroll(r.id), `${r.number} voided`)}><Ban className="w-3.5 h-3.5" />Void</Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <RunDialog open={open} onClose={() => setOpen(false)} people={data?.people || []} settings={data?.settings} run={editing} onSaved={load} />
    </div>
  );
}
